import React, { useState, useEffect, useCallback } from 'react';
import {
	Image,
	Video,
	FileText,
	Headphones,
	ChevronDown,
	Plus,
	Trash2,
	Settings,
	Info,
	Upload,
	X,
	AlertTriangle
} from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '../../../components/ui/dialog';
import { toast } from 'react-toastify';

const { ipcRenderer } = window.require('electron');

const ImagePreview = ({ filePath, filename }) => {
	const [previewUrl, setPreviewUrl] = useState(null);
	const [showFallback, setShowFallback] = useState(false);

	useEffect(() => {
		const loadImage = async () => {
			try {
				if (!filePath) {
					setShowFallback(true);
					return;
				}

				const fs = window.require('fs');
				
				// Verificar se o arquivo existe
				if (!fs.existsSync(filePath)) {
					setShowFallback(true);
					return;
				}

				const imageBuffer = fs.readFileSync(filePath);
				const blob = new Blob([imageBuffer]);
				const url = URL.createObjectURL(blob);
				setPreviewUrl(url);
			} catch (error) {
				console.error('Erro ao carregar imagem:', error);
				setShowFallback(true);
			}
		};

		if (filePath) {
			loadImage();
		} else {
			setShowFallback(true);
		}

		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [filePath]);

	if (showFallback || !previewUrl) {
		return (
			<div className="flex items-center justify-center p-8">
				<div className="text-center">
					<Image className="h-12 w-12 mx-auto mb-2 text-emeraldLight" />
					<p className="text-sm text-textOnDarkStrong font-medium">Imagem</p>
					<p className="text-xs text-grayText mt-1">{filename || 'Arquivo não encontrado'}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-2">
			<img 
				src={previewUrl}
				alt={filename || 'Preview'}
				className="w-full max-h-48 object-contain rounded-lg bg-blackBase/20"
				onError={() => setShowFallback(true)}
			/>
		</div>
	);
};

const AIMediaSection = ({
	instanceId,
	showAIMediaSettings,
	toggleAIMediaSettings,
	settings,
	knowledgeBaseActive,
	onSettingsChange
}) => {
	const [mediaList, setMediaList] = useState([]);
	const [mediaGroups, setMediaGroups] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showPreviewModal, setShowPreviewModal] = useState(false);
	const [selectedMedia, setSelectedMedia] = useState(null);
	const [selectedGroup, setSelectedGroup] = useState(null);
	const [mediaFiles, setMediaFiles] = useState([]);
	const [batchTitle, setBatchTitle] = useState(''); // Título compartilhado para batch
	const [batchDescription, setBatchDescription] = useState(''); // Descrição compartilhada para batch
	const [uploadLoading, setUploadLoading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
	const [uploadResults, setUploadResults] = useState({ results: [], errors: [] });
	const [fileSelectionInProgress, setFileSelectionInProgress] = useState(false);
	
	// Função para sanitizar nome e mostrar preview
	const sanitizeFunctionName = (title) => {
		if (!title) return '';
		
		let sanitized = 'send_' + title
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '') // Remove acentos
			.replace(/[^a-z0-9\s]/g, '')     // Remove caracteres especiais
			.replace(/\s+/g, '_')             // Espaços para underscore
			.replace(/_{2,}/g, '_')           // Remove underscores duplicados
			.replace(/^_|_$/g, '');           // Remove underscore no início/fim
			
		// Truncar para máximo 60 caracteres
		if (sanitized.length > 60) {
			sanitized = sanitized.substring(0, 60).replace(/_$/, '');
		}
		
		return sanitized;
	};

	const loadMediaList = useCallback(async () => {
		console.log('📋 [AIMediaSection] ========== INÍCIO LOADMEDIALIST ==========');
		console.log('📋 [AIMediaSection] InstanceId:', instanceId);

		if (!instanceId) {
			console.log('📋 [AIMediaSection] ❌ Sem instanceId - retornando');
			return;
		}

		setLoading(true);
		try {
			console.log('📋 [AIMediaSection] Chamando IPC list-ai-media para instanceId:', instanceId);
			const result = await ipcRenderer.invoke('list-ai-media', instanceId);
			console.log('📋 [AIMediaSection] Resultado do IPC list-ai-media:', result);

			if (result.success) {
				const mediaList = result.mediaList || [];
				const mediaGroups = result.mediaGroups || [];
				console.log('📋 [AIMediaSection] ✅ Mídias carregadas com sucesso:', {
					individualCount: mediaList.length,
					groupsCount: mediaGroups.length,
					mediaList: mediaList.map(media => ({
						id: media.id,
						title: media.title,
						mediaType: media.mediaType,
						fileExists: media.fileExists
					})),
					mediaGroups: mediaGroups.map(group => ({
						id: group.groupId,
						description: group.description,
						mediaCount: group.mediaItems.length
					}))
				});
				setMediaList(mediaList);
				setMediaGroups(mediaGroups);
			} else {
				console.error('📋 [AIMediaSection] ❌ Erro ao carregar mídias:', result.error);
				toast.error('Erro ao carregar mídias da IA');
			}
		} catch (error) {
			console.error('📋 [AIMediaSection] ❌ Exceção ao carregar mídias:', error);
			toast.error('Erro ao carregar mídias da IA');
		} finally {
			setLoading(false);
			console.log('📋 [AIMediaSection] ========== FIM LOADMEDIALIST ==========');
		}
	}, [instanceId]);

	useEffect(() => {
		if (showAIMediaSettings && instanceId) {
			loadMediaList();
		}
	}, [showAIMediaSettings, instanceId, loadMediaList]);

	useEffect(() => {
		if (!instanceId) return;
		const handler = () => loadMediaList();
		window.addEventListener("instance-data-imported", handler);
		return () => window.removeEventListener("instance-data-imported", handler);
	}, [instanceId, loadMediaList]);

	// Funções para gerenciar o estado unificado do modal
	const resetUploadModal = () => {
		setMediaFiles([]);
		setBatchTitle('');
		setBatchDescription('');
		setUploadResults({ results: [], errors: [] });
		setUploadProgress({ current: 0, total: 0 });
		setFileSelectionInProgress(false);
	};

	const openUploadModal = () => {
		resetUploadModal();
		setShowUploadModal(true);
	};

	const closeUploadModal = () => {
		setShowUploadModal(false);
		resetUploadModal();
	};


	const handleSelectFiles = async () => {
		if (fileSelectionInProgress) {
			return;
		}

		setFileSelectionInProgress(true);
		try {
			// Usar o seletor batch que permite múltipla seleção
			const result = await ipcRenderer.invoke('select-ai-media-files-batch', {
				mediaTypes: ['image', 'video', 'audio', 'document']
			});

			if (result.success && !result.canceled) {
				if (result.errors && result.errors.length > 0) {
					result.errors.forEach(err => {
						toast.error(`${err.filename}: ${err.error}`);
					});
				}

				if (result.filePaths.length === 0) {
					toast.warning('Nenhum arquivo válido selecionado.');
					return;
				}

				const filesWithMetadata = result.filePaths.map((filePath, index) => {
					const filename = filePath.split('/').pop() || filePath.split('\\').pop();
					const extension = filename.toLowerCase().split('.').pop();
					let mediaType = 'document';
					if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
						mediaType = 'image';
					} else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
						mediaType = 'video';
					} else if (['mp3', 'ogg', 'opus', 'm4a', 'wav', 'aac', 'flac', 'wma'].includes(extension)) {
						mediaType = 'audio';
					}

					const baseFile = {
						filePath,
						filename,
						mediaType,
						title: filename.replace(/\.[^/.]+$/, ""),
						id: `file_${index}_${Date.now()}`
					};

					if (result.filePaths.length === 1) {
						baseFile.description = '';
					}

					return baseFile;
				});

				setMediaFiles(filesWithMetadata);
			}
		} catch (error) {
			console.error('Erro ao selecionar arquivos:', error);
			toast.error('Erro ao selecionar arquivos');
		} finally {
			setFileSelectionInProgress(false);
		}
	};

	const handleAddMoreFiles = async () => {
		if (fileSelectionInProgress) {
			return;
		}

		setFileSelectionInProgress(true);
		try {
			// Usar o seletor batch que permite múltipla seleção
			const result = await ipcRenderer.invoke('select-ai-media-files-batch', {
				mediaTypes: ['image', 'video', 'audio', 'document']
			});

			if (result.success && !result.canceled) {
				if (result.errors && result.errors.length > 0) {
					result.errors.forEach(err => {
						toast.error(`${err.filename}: ${err.error}`);
					});
				}

				if (result.filePaths.length === 0) {
					toast.warning('Nenhum arquivo válido selecionado.');
					return;
				}

				const newFilesWithMetadata = result.filePaths.map((filePath, index) => {
					const filename = filePath.split('/').pop() || filePath.split('\\').pop();
					const extension = filename.toLowerCase().split('.').pop();
					let mediaType = 'document';
					if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
						mediaType = 'image';
					} else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
						mediaType = 'video';
					} else if (['mp3', 'ogg', 'opus', 'm4a', 'wav', 'aac', 'flac', 'wma'].includes(extension)) {
						mediaType = 'audio';
					}

					const baseFile = {
						filePath,
						filename,
						mediaType,
						title: filename.replace(/\.[^/.]+$/, ""),
						id: `file_${Date.now()}_${index}_${Math.random().toString(36).substring(2, 11)}`
					};

					if (result.filePaths.length === 1) {
						baseFile.description = '';
					}

					return baseFile;
				});

				// Validar duplicatas
				const existingPaths = mediaFiles.map(file => file.filePath);
				const uniqueNewFiles = newFilesWithMetadata.filter(newFile =>
					!existingPaths.includes(newFile.filePath)
				);

				if (uniqueNewFiles.length === 0) {
					toast.warning('Todos os arquivos selecionados já estão na lista');
					return;
				}

				if (uniqueNewFiles.length < newFilesWithMetadata.length) {
					const duplicateCount = newFilesWithMetadata.length - uniqueNewFiles.length;
					toast.info(`${duplicateCount} arquivo(s) duplicado(s) foram ignorado(s)`);
				}

				// Adicionar os novos arquivos aos existentes
				setMediaFiles(prevFiles => [...prevFiles, ...uniqueNewFiles]);

				if (uniqueNewFiles.length > 0) {
					toast.success(`${uniqueNewFiles.length} arquivo(s) adicionado(s) com sucesso`);
				}
			}
		} catch (error) {
			console.error('Erro ao adicionar mais arquivos:', error);
			toast.error('Erro ao adicionar mais arquivos');
		} finally {
			setFileSelectionInProgress(false);
		}
	};

	const handleUnifiedUpload = async () => {
		console.log('🎯 [AIMediaSection] ========== INÍCIO HANDLEUNIFIEDUPLOAD ==========');
		console.log('🎯 [AIMediaSection] Total de arquivos:', mediaFiles.length);

		if (mediaFiles.length === 0) {
			toast.error('Selecione pelo menos um arquivo');
			return;
		}

		// Auto-detectar o modo baseado no número de arquivos
		const isAutoDetectedBatch = mediaFiles.length > 1;
		
		// Validar campos obrigatórios baseado no modo
		if (isAutoDetectedBatch) {
			// Batch: validar título e descrição global
			if (!batchTitle.trim()) {
				toast.error('Preencha o título do conjunto');
				return;
			}
			if (batchTitle.length > 50) {
				toast.error('Título do conjunto deve ter no máximo 50 caracteres');
				return;
			}
			if (!batchDescription.trim()) {
				toast.error('Preencha a descrição compartilhada do conjunto');
				return;
			}
			if (batchDescription.length > 200) {
				toast.error('Descrição do conjunto deve ter no máximo 200 caracteres');
				return;
			}
			
			const invalidFiles = mediaFiles.filter(file => !file.filePath);
			if (invalidFiles.length > 0) {
				toast.error(`${invalidFiles.length} arquivo(s) inválido(s)`);
				return;
			}
		} else {
			// Single: validar título + descrição individual
			const file = mediaFiles[0];
			if (!file.filePath || !file.title.trim() || !file.description.trim()) {
				toast.error('Preencha todos os campos obrigatórios');
				return;
			}
			if (file.title.length > 50) {
				toast.error('Título da mídia deve ter no máximo 50 caracteres');
				return;
			}
			if (file.description.length > 200) {
				toast.error('Descrição da mídia deve ter no máximo 200 caracteres');
				return;
			}
		}

		setUploadLoading(true);
		setUploadProgress({ current: 0, total: mediaFiles.length });
		setUploadResults({ results: [], errors: [] });

		try {
			let result;
			
			if (!isAutoDetectedBatch) {
				// Upload único
				const mediaData = mediaFiles[0];
				console.log('🎯 [AIMediaSection] Auto-detectado: Upload único');
				result = await ipcRenderer.invoke('add-ai-media', {
					instanceId,
					filePath: mediaData.filePath,
					mediaType: mediaData.mediaType,
					title: mediaData.title,
					description: mediaData.description
				});

				if (result.success) {
					toast.success('Mídia adicionada com sucesso!');
					closeUploadModal();
					loadMediaList();
				} else {
					toast.error(result.error || 'Erro ao adicionar mídia');
				}
			} else {
				// Upload batch com título e descrição compartilhados
				console.log('🎯 [AIMediaSection] Auto-detectado: Upload em batch');
				console.log('🎯 [AIMediaSection] Título compartilhado:', batchTitle);
				console.log('🎯 [AIMediaSection] Descrição compartilhada:', batchDescription);
				
				result = await ipcRenderer.invoke('add-ai-media-batch', {
					instanceId,
					title: batchTitle, // Título compartilhado
					description: batchDescription, // Descrição compartilhada
					mediaFiles: mediaFiles.map(file => ({
						filePath: file.filePath,
						mediaType: file.mediaType
						// Note: sem título ou descrição individual
					}))
				});

				setUploadResults(result);

				if (result.success) {
					toast.success(`${result.summary.successful} mídia(s) adicionada(s) com sucesso!`);
					if (result.summary.failed > 0) {
						toast.warning(`${result.summary.failed} arquivo(s) falharam no upload`);
					}
					closeUploadModal();
					loadMediaList();
				} else {
					toast.error(`Erro no upload: ${result.summary.failed}/${result.summary.total} falharam`);
				}
			}
		} catch (error) {
			console.error('🎯 [AIMediaSection] ❌ Exceção ao fazer upload:', error);
			toast.error('Erro ao fazer upload das mídias');
		} finally {
			setUploadLoading(false);
			console.log('🎯 [AIMediaSection] ========== FIM HANDLEUNIFIEDUPLOAD ==========');
		}
	};

	const handleUpdateMedia = async () => {
		const currentItem = selectedGroup || selectedMedia;
		if (!currentItem || !currentItem.title?.trim() || !currentItem.description?.trim()) {
			toast.error('Preencha todos os campos obrigatórios');
			return;
		}

		setLoading(true);
		try {
			const result = selectedGroup 
				? await ipcRenderer.invoke('update-media-group', {
						instanceId,
						groupId: selectedGroup.groupId,
						updates: {
							title: selectedGroup.title,
							description: selectedGroup.description
						}
					})
				: await ipcRenderer.invoke('update-ai-media', {
						instanceId,
						mediaId: selectedMedia.id,
						updates: {
							title: selectedMedia.title,
							description: selectedMedia.description
						}
					});

			if (result.success) {
				toast.success(selectedGroup ? 'Grupo atualizado com sucesso!' : 'Mídia atualizada com sucesso!');
				setShowEditModal(false);
				setSelectedMedia(null);
				setSelectedGroup(null);
				loadMediaList();
			} else {
				toast.error(result.error || (selectedGroup ? 'Erro ao atualizar grupo' : 'Erro ao atualizar mídia'));
			}
		} catch (error) {
			console.error('Erro ao atualizar:', error);
			toast.error(selectedGroup ? 'Erro ao atualizar grupo' : 'Erro ao atualizar mídia');
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveMedia = async (mediaId) => {
		if (!confirm('Tem certeza que deseja remover esta mídia?')) {
			return;
		}

		setLoading(true);
		try {
			const result = await ipcRenderer.invoke('remove-ai-media', {
				instanceId,
				mediaId
			});

			if (result.success) {
				toast.success('Mídia removida com sucesso!');
				loadMediaList();
			} else {
				toast.error(result.error || 'Erro ao remover mídia');
			}
		} catch (error) {
			console.error('Erro ao remover mídia:', error);
			toast.error('Erro ao remover mídia');
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveGroup = async (groupId) => {
		if (!confirm('Tem certeza que deseja remover este grupo de mídias?')) {
			return;
		}

		try {
			const result = await ipcRenderer.invoke('remove-media-group', {
				instanceId,
				groupId
			});

			if (result.success) {
				toast.success('Grupo removido com sucesso!');
				loadMediaList();
			} else {
				toast.error(result.error || 'Erro ao remover grupo');
			}
		} catch (error) {
			console.error('Erro ao remover grupo:', error);
			toast.error('Erro ao remover grupo');
		}
	};

	const handleUpdateGroup = async (groupId, updates) => {
		try {
			const result = await ipcRenderer.invoke('update-media-group', {
				instanceId,
				groupId,
				updates
			});

			if (result.success) {
				toast.success('Grupo atualizado com sucesso!');
				loadMediaList();
				return true;
			} else {
				toast.error(result.error || 'Erro ao atualizar grupo');
				return false;
			}
		} catch (error) {
			console.error('Erro ao atualizar grupo:', error);
			toast.error('Erro ao atualizar grupo');
			return false;
		}
	};


	const handleFieldChange = (fileId, field, value) => {
		setMediaFiles(prev => 
			prev.map(file => 
				file.id === fileId 
					? { ...file, [field]: value }
					: file
			)
		);
	};

	const handleRemoveFile = (fileId) => {
		setMediaFiles(prev => prev.filter(file => file.id !== fileId));
	};

	const getMediaIcon = (mediaType) => {
		// Normalizar o tipo de mídia (tratar tanto 'type' quanto 'mediaType')
		const normalizedType = mediaType || 'document';
		switch (normalizedType) {
			case 'image':
				return <Image className="h-4 w-4" />;
			case 'video':
				return <Video className="h-4 w-4" />;
			case 'audio':
				return <Headphones className="h-4 w-4" />;
			case 'document':
				return <FileText className="h-4 w-4" />;
			default:
				return <FileText className="h-4 w-4" />;
		}
	};

	const getMediaTypeColor = (mediaType) => {
		// Normalizar o tipo de mídia (tratar tanto 'type' quanto 'mediaType')
		const normalizedType = mediaType || 'document';
		switch (normalizedType) {
			case 'image':
				return 'text-emeraldLight';
			case 'video':
				return 'text-cyanPrimary';
			case 'audio':
				return 'text-purple-400';
			case 'document':
				return 'text-tealPrimary';
			default:
				return 'text-grayText';
		}
	};

	return (
		<>
			<div className="relative overflow-hidden bg-gradient-to-br from-emeraldPrimary/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldPrimary/25 transition-shadow duration-200">
				<button
					type="button"
					className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
					onClick={toggleAIMediaSettings}
					aria-expanded={showAIMediaSettings}
				>
					<h3 className="font-medium flex items-center">
						<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emeraldPrimary/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60 mr-3">
							<Image className="h-4 w-4 text-emeraldLight" />
						</div>
						<span className="text-textPrimary">Mídia com IA</span>
					</h3>
					<div
						className={`transform transition-transform duration-200 ${showAIMediaSettings ? "rotate-180" : ""}`}
					>
						<ChevronDown
							className="h-5 w-5 text-textPrimary"
							aria-hidden="true"
						/>
					</div>
				</button>

				<div
					className={`transition-all duration-300 ease-in-out ${showAIMediaSettings ? "max-h-[800px] opacity-100 overflow-y-auto" : "max-h-0 opacity-0 overflow-hidden"}`}
				>
					<div className="px-4 pb-4">
						<div className="mb-4 p-3 bg-emeraldPrimary/10 border border-emeraldPrimary/30 rounded-lg">
							<div className="flex items-center text-emeraldPrimary mb-2">
								<Info className="h-5 w-5 mr-2" />
								<span className="font-medium">Como funciona</span>
							</div>
							<p className="text-emeraldLight text-sm">
								Cadastre mídias com descrições para que a IA possa enviá-las automaticamente quando apropriado.
							</p>
							<div className="mt-3 grid grid-cols-2 gap-2 text-xs">
								<div className="flex items-center gap-1.5">
									<Image className="h-3.5 w-3.5 text-emeraldLight" />
									<span className="text-emeraldLight/80">JPG, PNG, GIF, WEBP</span>
								</div>
								<div className="flex items-center gap-1.5">
									<Video className="h-3.5 w-3.5 text-cyanPrimary" />
									<span className="text-cyanPrimary/80">MP4, AVI, MOV, MKV, WEBM</span>
								</div>
								<div className="flex items-center gap-1.5">
									<Headphones className="h-3.5 w-3.5 text-purple-400" />
									<span className="text-purple-400/80">MP3, OGG, OPUS, M4A, WAV, AAC, FLAC</span>
								</div>
								<div className="flex items-center gap-1.5">
									<FileText className="h-3.5 w-3.5 text-tealPrimary" />
									<span className="text-tealPrimary/80">PDF, DOC, DOCX, TXT, RTF</span>
								</div>
							</div>
						</div>

						<div className={`flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-emeraldBase/25 mb-4 ${knowledgeBaseActive ? 'opacity-60' : ''}`}>
							<div className="flex items-center gap-3">
								<div>
									<label className="text-sm font-medium text-textPrimary">
										Habilitar Mídia com IA
									</label>
									<p className="text-xs text-grayText">
										Permite que a IA envie mídias cadastradas automaticamente
									</p>
								</div>
							</div>
							<label className={`relative inline-flex items-center ${knowledgeBaseActive ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
								<input
									type="checkbox"
									name="AI_MEDIA_TOOLS_ENABLED"
									checked={knowledgeBaseActive ? false : settings?.AI_MEDIA_TOOLS_ENABLED !== false}
									onChange={onSettingsChange}
									disabled={knowledgeBaseActive}
									className="sr-only peer"
								/>
								<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep peer-disabled:opacity-50"></div>
							</label>
						</div>

						{settings?.AI_MEDIA_TOOLS_ENABLED === false && !knowledgeBaseActive && (
							<div className="mb-4 flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
								<AlertTriangle className="w-4 h-4 flex-shrink-0" />
								<span>As mídias cadastradas não serão utilizadas pela IA enquanto esta opção estiver desabilitada.</span>
							</div>
						)}

						{knowledgeBaseActive && (
							<div className="mb-4 flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
								<AlertTriangle className="w-4 h-4 flex-shrink-0" />
								<span>Mídia com IA temporariamente desabilitada enquanto a Base de Conhecimento está ativa (limitação da API Gemini)</span>
							</div>
						)}

						<div className="relative z-10 bg-grayBase/80 rounded-lg p-5 space-y-4 shadow-inner border border-emeraldMedium/20 backdrop-blur-sm">
							<div className="flex items-center justify-between">
								<h4 className="text-sm font-medium text-textPrimary">Mídias Cadastradas</h4>
								<button
									type="button"
									onClick={() => openUploadModal()}
									className="flex items-center gap-2 px-3 py-2 bg-emeraldPrimary hover:bg-emeraldDeep text-textPrimary rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emeraldPrimary"
									disabled={loading}
								>
									<Plus className="h-4 w-4" />
									Adicionar Mídias
								</button>
							</div>

							{loading ? (
								<div className="text-center py-8 text-textSecondary">
									Carregando...
								</div>
							) : mediaGroups.length === 0 && mediaList.length === 0 ? (
								<div className="text-center py-8 text-textSecondary">
									<Image className="h-12 w-12 mx-auto mb-3 opacity-50" />
									<p>Nenhuma mídia cadastrada</p>
									<p className="text-sm">Clique em "Adicionar Mídia" para começar</p>
								</div>
							) : (
								<div className="space-y-3">
									{/* Grupos de mídias */}
									{mediaGroups.map((group) => (
										<div
											key={group.groupId}
											className="flex items-center justify-between p-3 bg-emeraldPrimary/10 rounded-lg border border-emeraldLight/30"
										>
											<div className="flex items-center gap-3 flex-1">
												<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emeraldPrimary/20 text-emeraldLight">
													<span className="text-sm font-bold">{group.mediaItems.length}</span>
												</div>
												<div className="flex-1">
													<h5 className="text-sm font-medium text-textPrimary">{group.title || `Grupo de ${group.mediaItems.length} mídia${group.mediaItems.length > 1 ? 's' : ''}`}</h5>
													<p className="text-xs text-textSecondary truncate">{group.description}</p>
													<span className="text-xs text-emeraldLight">{group.mediaItems.length} arquivo{group.mediaItems.length !== 1 ? 's' : ''}</span>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<button
											type="button"
											onClick={() => {
												setSelectedGroup({ ...group });
												setShowEditModal(true);
											}}
											className="p-2 text-grayText hover:text-emeraldLight transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											title="Configurar grupo"
											>
												<Settings className="h-4 w-4" />
										</button>
												<button
											type="button"
											onClick={() => handleRemoveGroup(group.groupId)}
											className="p-2 text-grayText hover:text-redBase transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											title="Remover grupo"
											disabled={loading}
											>
												<Trash2 className="h-4 w-4" />
										</button>
										</div>
										</div>
									))}
									
									{/* Mídias individuais */}
									{mediaList.map((media) => (
										<div
											key={media.id}
											className="flex items-center justify-between p-3 bg-grayContent/30 rounded-lg border border-grayLight/30"
										>
											<div className="flex items-center gap-3 flex-1">
												<div className={`${getMediaTypeColor(media.mediaType || media.type)}`}>
													{getMediaIcon(media.mediaType || media.type)}
												</div>
												<div className="flex-1">
													<h5 className="text-sm font-medium text-textPrimary">{media.title}</h5>
													<p className="text-xs text-textSecondary truncate">{media.description}</p>
													<span className="text-xs text-grayText capitalize">{media.mediaType || media.type}</span>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<button
											type="button"
											onClick={() => {
												setSelectedMedia({ ...media });
												setShowEditModal(true);
											}}
											className="p-2 text-grayText hover:text-emeraldLight transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											title="Ver configurações"
											>
												<Settings className="h-4 w-4" />
										</button>
												<button
											type="button"
											onClick={() => handleRemoveMedia(media.id)}
											className="p-2 text-grayText hover:text-redBase transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											title="Remover"
											disabled={loading}
											>
												<Trash2 className="h-4 w-4" />
										</button>
										</div>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Modal unificado para upload */}
			<Dialog open={showUploadModal} onOpenChange={(open) => {
				if (!open) {
					closeUploadModal();
				}
			}}>
				<DialogContent
					className={`${mediaFiles.length > 1 ? 'max-w-4xl max-h-[90vh] overflow-y-auto' : 'max-w-[500px]'} bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-blackBase/50 relative overflow-hidden`}
					style={{
						position: 'fixed',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						zIndex: 9999
					}}
				>
					{/* Efeitos de fundo glassmorphism */}
					<div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
					<div className="absolute top-16 left-24 w-32 h-32 bg-effectBlue rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>
					<div className="absolute bottom-16 right-16 w-36 h-36 bg-effectPurple rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>

					{/* Botão X customizado */}
					<button
						type="button"
						onClick={closeUploadModal}
						className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-textPrimary hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
					>
						<X className="w-4 h-4" />
					</button>

					<DialogHeader className="relative z-10">
						<DialogTitle className="flex items-center gap-4 text-textPrimary">
							<div className={`relative flex items-center justify-center w-12 h-12 rounded-xl shadow-lg border border-modalOverlayMedium ${
			mediaFiles.length <= 1
				? 'bg-gradient-to-br from-grayContent/80 via-statusInfo/30 to-purplePrimary/40'
				: 'bg-gradient-to-br from-emeraldPrimary/80 via-emeraldMedium/60 to-emeraldDeep/80'
		}`}>
								{mediaFiles.length <= 1 ? (
									<span className="text-xl drop-shadow-sm">📁</span>
								) : (
									<Upload className="h-6 w-6 text-textPrimary drop-shadow-sm" />
								)}
								<div className={`absolute inset-0 rounded-xl animate-pulse-subtle ${
					mediaFiles.length <= 1
						? 'bg-gradient-to-br from-statusInfo/10 to-purplePrimary/8'
						: 'bg-gradient-to-br from-emeraldPrimary/10 to-emeraldDeep/8'
				}`}></div>
							</div>
							<div>
								<h2 className="text-xl font-semibold">
									{mediaFiles.length === 0 
										? 'Adicionar Mídias'
										: mediaFiles.length === 1 
											? 'Adicionar Mídia'
											: `Upload ${mediaFiles.length} Mídias`
									}
								</h2>
								<p className="text-sm text-textSecondary">
									{mediaFiles.length === 0
										? 'Selecione um ou mais arquivos de mídia'
										: mediaFiles.length === 1
											? 'Configure os dados da sua mídia'
											: `Configure os dados das suas ${mediaFiles.length} mídias`
									}
								</p>
							</div>
						</DialogTitle>
					</DialogHeader>

					{/* Conteúdo do modal - sempre adaptativo */}
					<div className="flex flex-col gap-6 relative z-10 max-h-[60vh] overflow-y-auto">
						{/* Área de seleção de arquivos */}
						{mediaFiles.length === 0 ? (
							<div className="text-center py-12">
								<button
									type="button"
									onClick={handleSelectFiles}
									className="w-full rounded-xl border-2 border-dashed border-emeraldBase/30 bg-dashboardAccent/20 backdrop-blur-xl px-8 py-12 text-center transition-all duration-200 hover:border-emeraldMedium/50 hover:bg-dashboardAccent/30 focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg group disabled:opacity-50 disabled:cursor-not-allowed"
									disabled={uploadLoading || fileSelectionInProgress}
								>
									<div className="flex flex-col items-center gap-4">
										<div className="flex items-center justify-center w-16 h-16 rounded-full bg-emeraldBase/20 group-hover:bg-emeraldBase/30 transition-colors duration-200">
											<Upload className="h-8 w-8 text-emeraldLight" />
										</div>
										<div className="space-y-2">
											<h3 className="text-lg font-medium text-textPrimary">
												{fileSelectionInProgress ? 'Selecionando Arquivos...' : 'Selecionar Arquivos de Mídia'}
											</h3>
											<p className="text-sm text-grayText">
												{fileSelectionInProgress ? 'Aguarde enquanto o seletor é aberto' : 'Clique para selecionar um ou mais arquivos'}
											</p>
											{!fileSelectionInProgress && (
												<p className="text-xs text-grayText">
													Suporta imagens, vídeos, áudios e documentos
												</p>
											)}
										</div>
									</div>
								</button>
							</div>
						) : mediaFiles.length === 1 ? (
							/* Layout Single - Um arquivo */
							<>
								{/* Seleção de tipo de mídia para arquivo único */}
								<div>
									<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
										Tipo de Mídia
									</label>
									<div className="relative">
										<select
											value={mediaFiles[0].mediaType}
											onChange={(e) => handleFieldChange(mediaFiles[0].id, 'mediaType', e.target.value)}
											className="w-full rounded-lg border border-emeraldBase/25 bg-grayContent/30 backdrop-blur-xl px-4 py-3 pr-10 text-textPrimary focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg appearance-none cursor-pointer"
											disabled={uploadLoading}
										>
											<option value="image">Imagem</option>
											<option value="video">Vídeo</option>
											<option value="audio">Áudio</option>
											<option value="document">Documento</option>
										</select>
										<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
											<svg className="h-5 w-5 text-grayText" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
											</svg>
										</div>
									</div>
								</div>

								{/* Header com ações para arquivo único */}
								<div className="flex items-center justify-between">
									<h3 className="text-lg font-semibold text-textPrimary">
										Arquivo Selecionado
									</h3>
									<div className="flex gap-2">
										<button
											type="button"
											onClick={handleAddMoreFiles}
											className="flex items-center gap-2 px-3 py-2 bg-grayBase/60 hover:bg-grayBase/80 text-textSecondary rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
											disabled={uploadLoading || fileSelectionInProgress}
										>
											<Plus className="h-4 w-4" />
											Adicionar Mais
										</button>
									</div>
								</div>

								{/* Arquivo selecionado */}
								<div>
									<div className="bg-grayContent/30 rounded-lg p-4 border border-modalOverlayMedium">
										<div className="flex items-center gap-3">
											<div className={`${getMediaTypeColor(mediaFiles[0].mediaType)}`}>
												{getMediaIcon(mediaFiles[0].mediaType)}
											</div>
											<div className="flex-1">
												<p className="text-sm font-medium text-textPrimary">
													{mediaFiles[0].filename || mediaFiles[0].filePath?.split('/').pop() || mediaFiles[0].filePath?.split('\\').pop()}
												</p>
												<p className="text-xs text-textSecondary capitalize">{mediaFiles[0].mediaType}</p>
											</div>
											<button
												type="button"
												onClick={handleSelectFiles}
												className="text-emeraldLight hover:text-emeraldBase transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
												disabled={uploadLoading || fileSelectionInProgress}
											>
												{fileSelectionInProgress ? 'Selecionando...' : 'Alterar'}
											</button>
										</div>
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
										Título * 
										<span className="text-xs text-grayText ml-2">
											(máx. 50 caracteres)
										</span>
									</label>
									<input
										type="text"
										value={mediaFiles[0].title}
										onChange={(e) => {
											const value = e.target.value.slice(0, 50);
											handleFieldChange(mediaFiles[0].id, 'title', value);
										}}
										placeholder="Ex: Logo da empresa"
										maxLength={50}
										className="w-full rounded-lg border border-emeraldBase/25 bg-grayContent/30 backdrop-blur-xl px-4 py-3 text-textPrimary focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText"
										disabled={uploadLoading}
									/>
									{mediaFiles[0].title && (
										<div className="mt-2 space-y-1">
											<div className="flex justify-between text-xs">
												<span className="text-grayText">
													Caracteres: {mediaFiles[0].title.length}/50
												</span>
												{mediaFiles[0].title.length > 45 && (
													<span className="text-warningText">
														Próximo do limite
													</span>
												)}
											</div>
											{mediaFiles[0].title && (
												<div className="text-xs text-cyanPrimary bg-modalOverlaySoft rounded px-2 py-1">
													Nome da função: <code className="font-mono">{sanitizeFunctionName(mediaFiles[0].title)}</code>
												</div>
											)}
											{/[áàâãéèêíìîóòôõúùûç]/i.test(mediaFiles[0].title) && (
												<div className="text-xs text-warningText bg-warningBg rounded px-2 py-1">
													⚠️ Acentos serão removidos automaticamente
												</div>
											)}
										</div>
									)}
								</div>

								<div>
									<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
										Descrição *
										<span className="text-xs text-grayText ml-2">
											(máx. 200 caracteres)
										</span>
									</label>
									<textarea
										value={mediaFiles[0].description}
										onChange={(e) => {
											const value = e.target.value.slice(0, 200);
											handleFieldChange(mediaFiles[0].id, 'description', value);
										}}
										placeholder="Descreva quando a IA deve enviar esta mídia..."
										maxLength={200}
										rows={3}
										className="w-full rounded-lg border border-emeraldBase/25 bg-grayContent/30 backdrop-blur-xl px-4 py-3 text-textPrimary focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText resize-none"
										disabled={uploadLoading}
									/>
									{mediaFiles[0].description && (
										<div className="mt-1 flex justify-between text-xs">
											<span className="text-grayText">
												Caracteres: {mediaFiles[0].description.length}/200
											</span>
											{mediaFiles[0].description.length > 180 && (
												<span className="text-warningText">
													Próximo do limite
												</span>
											)}
										</div>
									)}
								</div>
							</>
						) : (
							/* Layout Batch - Múltiplos arquivos */
				<>
					{/* Título compartilhado para o batch */}
					<div>
						<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
							Título do Conjunto *
							<span className="text-xs text-grayText ml-2">
								(máx. 50 caracteres)
							</span>
						</label>
						<input
							type="text"
							value={batchTitle}
							onChange={(e) => setBatchTitle(e.target.value.slice(0, 50))}
							placeholder="Ex: Apartamento 2 quartos - Fotos e plantas"
							maxLength={50}
							className="w-full rounded-lg border border-emeraldBase/25 bg-grayContent/30 backdrop-blur-xl px-4 py-3 text-textPrimary focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText"
							disabled={uploadLoading}
						/>
						{batchTitle && (
							<div className="mt-2 space-y-1">
								<div className="flex justify-between text-xs">
									<span className="text-grayText">
										Caracteres: {batchTitle.length}/50
									</span>
									{batchTitle.length > 45 && (
										<span className="text-warningText">
											Próximo do limite
										</span>
									)}
								</div>
								{batchTitle && (
									<div className="text-xs text-cyanPrimary bg-modalOverlaySoft rounded px-2 py-1">
										Nome da função: <code className="font-mono">{sanitizeFunctionName(batchTitle)}</code>
									</div>
								)}
								{/[áàâãéèêíìîóòôõúùûç]/i.test(batchTitle) && (
									<div className="text-xs text-warningText bg-warningBg rounded px-2 py-1">
										⚠️ Acentos serão removidos automaticamente
									</div>
								)}
							</div>
						)}
					</div>

					{/* Descrição compartilhada para o batch */}
					<div>
						<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
							Descrição do Conjunto *
							<span className="text-xs text-grayText ml-2">
								(máx. 200 caracteres)
							</span>
						</label>
						<textarea
							value={batchDescription}
							onChange={(e) => setBatchDescription(e.target.value.slice(0, 200))}
							placeholder="Quando a IA deve enviar este conjunto de mídias? Ex: quando cliente perguntar sobre apartamento 2 quartos..."
							maxLength={200}
							rows={3}
							className="w-full rounded-lg border border-cyanPrimary/25 bg-grayContent/30 backdrop-blur-xl px-4 py-3 text-textPrimary focus:border-cyanSecondary focus:ring focus:ring-cyanSecondary/30 shadow-lg placeholder-grayText resize-none"
							disabled={uploadLoading}
						/>
						{batchDescription && (
							<div className="mt-1 flex justify-between text-xs">
								<span className="text-grayText">
									Caracteres: {batchDescription.length}/200
								</span>
								{batchDescription.length > 180 && (
									<span className="text-warningText">
										Próximo do limite
									</span>
								)}
							</div>
						)}
						<p className="text-xs text-grayText mt-1">
							Esta descrição será o gatilho para a IA enviar todas as {mediaFiles.length} mídias juntas
						</p>
								</div>

								{/* Header com ações */}
								<div className="flex items-center justify-between">
									<h3 className="text-lg font-semibold text-textPrimary">
										{mediaFiles.length} arquivo(s) selecionado(s)
									</h3>
									<div className="flex gap-2">
										<button
											type="button"
											onClick={handleAddMoreFiles}
											className="flex items-center gap-2 px-3 py-2 bg-grayBase/60 hover:bg-grayBase/80 text-textSecondary rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
											disabled={uploadLoading || fileSelectionInProgress}
										>
											<Plus className="h-4 w-4" />
											Adicionar Mais
										</button>
									</div>
								</div>

								{/* Lista de arquivos */}
								<div className="max-h-96 overflow-y-auto space-y-3 pr-2">
									{mediaFiles.map((file) => (
										<div key={file.id} className="bg-grayContent/30 rounded-lg p-4 border border-modalOverlayMedium">
											<div className="flex items-start gap-4">
												{/* Ícone do tipo de arquivo */}
												<div className={`flex-shrink-0 ${getMediaTypeColor(file.mediaType)} mt-1`}>
													{getMediaIcon(file.mediaType)}
												</div>
												
												{/* Campos de input */}
												<div className="flex-1 space-y-3">
													<div>
														<label className="block text-xs font-medium text-textOnDarkStrong mb-1">
															Arquivo: {file.filename || file.filePath?.split('/').pop() || file.filePath?.split('\\').pop()}
														</label>
													</div>
													
													<div>
														<label className="block text-xs font-medium text-textOnDarkStrong mb-1">
															Título *
														</label>
														<input
															type="text"
															value={file.title}
															onChange={(e) => handleFieldChange(file.id, 'title', e.target.value)}
															className="w-full rounded-md border border-modalOverlayMedium bg-grayContent/30 px-3 py-2 text-sm text-textPrimary focus:border-cyanPrimary focus:ring focus:ring-cyanPrimary/30 transition-all"
															placeholder="Ex: Imóvel - Sala"
															disabled={uploadLoading}
														/>
													</div>
												</div>
												
												{/* Botão remover */}
												<button
													type="button"
													onClick={() => handleRemoveFile(file.id)}
													className="flex-shrink-0 p-2 text-grayText hover:text-redBase transition-colors"
													disabled={uploadLoading}
													title="Remover arquivo"
												>
													<Trash2 className="h-4 w-4" />
												</button>
											</div>
										</div>
									))}
								</div>
							</>
						)}

						{/* Progress bar durante upload */}
						{uploadLoading && uploadProgress.total > 0 && (
						<div className="bg-grayContent/30 rounded-lg p-4 border border-modalOverlayMedium">
								<div className="flex items-center justify-between mb-2">
									<span className="text-sm font-medium text-textPrimary">
										Processando arquivos...
									</span>
									<span className="text-sm text-textSecondary">
										{uploadProgress.current} / {uploadProgress.total}
									</span>
								</div>
								<div className="w-full bg-grayBase/30 rounded-full h-2">
									<div 
										className="bg-cyanPrimary h-2 rounded-full transition-all duration-300"
										style={{ 
											width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%` 
										}}
									></div>
								</div>
							</div>
						)}

						{/* Resultados do upload */}
						{(uploadResults.results.length > 0 || uploadResults.errors.length > 0) && (
						<div className="bg-grayContent/30 rounded-lg p-4 border border-modalOverlayMedium">
								<h4 className="text-sm font-medium text-textPrimary mb-3">Resultado do Upload</h4>
								
								{uploadResults.results.length > 0 && (
									<div className="mb-3">
										<p className="text-sm text-emeraldLight mb-2">✅ {uploadResults.results.length} arquivo(s) processado(s) com sucesso</p>
									</div>
								)}
								
								{uploadResults.errors.length > 0 && (
									<div>
										<p className="text-sm text-redBase mb-2">❌ {uploadResults.errors.length} erro(s) encontrado(s):</p>
										<div className="space-y-1 max-h-32 overflow-y-auto">
											{uploadResults.errors.map((error, index) => (
												<div key={index} className="text-xs text-redBase bg-redBase/10 rounded px-2 py-1">
													{error.filename}: {error.error}
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						)}
					</div>

					{/* Botões de ação - fixos na parte inferior */}
					<div className="flex gap-3 pt-4 border-t border-modalOverlayMedium mt-4 relative z-10">
						<button
							type="button"
							onClick={closeUploadModal}
							className="flex-1 rounded-xl text-textPrimary px-4 py-3 font-medium transition-all duration-300 bg-gradient-to-r from-dangerFrom to-dangerTo hover:from-redMedium hover:to-redBase shadow-lg hover:shadow-dangerFrom/25 hover:scale-[1.02] cursor-pointer"
							disabled={uploadLoading}
						>
							<span className="relative z-10">Cancelar</span>
						</button>
						<button
							type="button"
							onClick={handleUnifiedUpload}
							className={`flex-1 flex items-center justify-center gap-2 rounded-lg backdrop-blur-xl px-4 py-3 text-sm font-semibold text-textPrimary transition-all duration-300 hover:shadow-xl shadow-lg hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed ${
					mediaFiles.length <= 1
						? 'bg-gradient-to-r from-emeraldPrimary/80 to-emeraldMedium/80 border border-emeraldLight/60 hover:from-emeraldMedium/90 hover:to-emeraldDeep/90 hover:border-emeraldBase/70 hover:shadow-emeraldBase/40 shadow-emeraldBase/25'
						: 'bg-gradient-to-r from-emeraldPrimary/80 to-emeraldDeep/80 border border-emeraldMedium/60 hover:from-emeraldMedium/90 hover:to-emeraldDeep/90 hover:border-emeraldBase/70 hover:shadow-emeraldBase/40 shadow-emeraldBase/25'
				}`}
							disabled={uploadLoading || mediaFiles.length === 0}
						>
							{uploadLoading ? (
								<>
									<svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
									</svg>
									<span>Processando...</span>
								</>
							) : (
								<>
									{mediaFiles.length <= 1 ? (
										<svg className="h-4 w-4 drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
										</svg>
									) : (
										<Upload className="h-4 w-4 drop-shadow-sm" />
									)}
									<span>
										{mediaFiles.length === 0 
											? 'Selecionar Arquivos'
											: mediaFiles.length === 1 
												? 'Adicionar Mídia' 
												: `Upload ${mediaFiles.length} Mídias`
										}
									</span>
								</>
							)}
						</button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Modal para editar mídia ou grupo */}
			<Dialog open={showEditModal && (selectedMedia || selectedGroup)} onOpenChange={(open) => {
				if (!open) {
					setShowEditModal(false);
					setSelectedMedia(null);
					setSelectedGroup(null);
				}
			}}>
				<DialogContent
					className="max-w-[500px] max-h-[85vh] bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-blackBase/50 relative overflow-hidden flex flex-col"
					style={{
						position: 'fixed',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						zIndex: 9999
					}}
				>
					{/* Efeitos de fundo glassmorphism */}
					<div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
					<div className="absolute top-16 left-24 w-32 h-32 bg-effectBlue rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>
					<div className="absolute bottom-16 right-16 w-36 h-36 bg-effectPurple rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>

					{/* Botão X customizado */}
					<button
						type="button"
						onClick={() => {
						setShowEditModal(false);
						setSelectedMedia(null);
						setSelectedGroup(null);
					}}
						className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-textPrimary hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
					>
						<X className="w-4 h-4" />
					</button>

					<DialogHeader className="relative z-10">
						<DialogTitle className="flex items-center gap-4 text-textPrimary">
					<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-grayContent/80 via-statusInfo/30 to-purplePrimary/40 shadow-lg border border-modalOverlayMedium">
								<span className="text-xl drop-shadow-sm">⚙️</span>
								<div className="absolute inset-0 bg-gradient-to-br from-statusInfo/10 to-purplePrimary/8 rounded-xl animate-pulse-subtle"></div>
							</div>
							<span className="text-xl font-semibold">Configurações da Mídia</span>
						</DialogTitle>
					</DialogHeader>

					{/* Conteúdo scrollável */}
					<div className="flex flex-col gap-6 relative z-10 flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(85vh - 180px)' }}>
						{/* Pré-visualização somente para mídias individuais */}
						{!selectedGroup && selectedMedia && (
							<div>
								<label className="block text-sm font-medium text-textOnDarkStrong mb-3">
									Pré-visualização
								</label>
								<div className="bg-dashboardAccent/20 rounded-lg p-4 border border-emeraldBase/25">
									{(selectedMedia?.mediaType === 'image' || selectedMedia?.type === 'image') ? (
										<div className="space-y-3">
											<div className="bg-dashboardAccent/30 rounded-lg p-4 border-2 border-dashed border-emeraldBase/30">
												<ImagePreview 
													filePath={selectedMedia?.filePath}
													filename={selectedMedia?.filename}
												/>
											</div>
										</div>
									) : (selectedMedia?.mediaType === 'video' || selectedMedia?.type === 'video') ? (
											<div className="space-y-3">
												<div className="flex items-center justify-center bg-dashboardAccent/30 rounded-lg p-8 border-2 border-dashed border-cyanPrimary/30">
													<div className="text-center">
														<Video className="h-12 w-12 mx-auto mb-2 text-cyanPrimary" />
														<p className="text-sm text-textOnDarkStrong font-medium">Vídeo</p>
														<p className="text-xs text-grayText mt-1">{selectedMedia?.filename || 'Arquivo não encontrado'}</p>
													</div>
												</div>
											</div>
									) : (selectedMedia?.mediaType === 'audio' || selectedMedia?.type === 'audio') ? (
										<div className="space-y-3">
											<div className="flex items-center justify-center bg-dashboardAccent/30 rounded-lg p-8 border-2 border-dashed border-purple-400/30">
												<div className="text-center">
													<Headphones className="h-12 w-12 mx-auto mb-2 text-purple-400" />
													<p className="text-sm text-textOnDarkStrong font-medium">Áudio</p>
													<p className="text-xs text-grayText mt-1">{selectedMedia?.filename || 'Arquivo não encontrado'}</p>
												</div>
											</div>
										</div>
									) : (
										<div className="space-y-3">
											<div className="flex items-center justify-center bg-dashboardAccent/30 rounded-lg p-8 border-2 border-dashed border-tealPrimary/30">
												<div className="text-center">
													<FileText className="h-12 w-12 mx-auto mb-2 text-tealPrimary" />
													<p className="text-sm text-textOnDarkStrong font-medium">Documento</p>
													<p className="text-xs text-grayText mt-1">{selectedMedia?.filename || 'Arquivo não encontrado'}</p>
												</div>
											</div>
										</div>
									)}
								</div>
							</div>
						)}

						<div>
							<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
								Título *
								<span className="text-xs text-grayText ml-2">
									(máx. 50 caracteres)
								</span>
							</label>
							<input
								type="text"
								value={selectedGroup ? (selectedGroup?.title || '') : (selectedMedia?.title || '')}
								onChange={(e) => {
									const value = e.target.value.slice(0, 50);
									if (selectedGroup) {
										setSelectedGroup(prev => ({ ...prev, title: value }));
									} else {
										setSelectedMedia(prev => ({ ...prev, title: value }));
									}
								}}
								maxLength={50}
								className="w-full rounded-lg border border-emeraldBase/25 bg-grayContent/30 backdrop-blur-xl px-4 py-3 text-textPrimary focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText"
							/>
							{(selectedGroup?.title || selectedMedia?.title) && (
								<div className="mt-2 space-y-1">
									<div className="flex justify-between text-xs">
										<span className="text-grayText">
											Caracteres: {(selectedGroup?.title || selectedMedia?.title || '').length}/50
										</span>
										{(selectedGroup?.title || selectedMedia?.title || '').length > 45 && (
											<span className="text-warningText">
												Próximo do limite
											</span>
										)}
									</div>
									{(selectedGroup?.title || selectedMedia?.title) && (
										<div className="text-xs text-cyanPrimary bg-modalOverlaySoft rounded px-2 py-1">
											Nome da função: <code className="font-mono">{sanitizeFunctionName(selectedGroup?.title || selectedMedia?.title)}</code>
										</div>
									)}
									{/[áàâãéèêíìîóòôõúùûç]/i.test(selectedGroup?.title || selectedMedia?.title) && (
										<div className="text-xs text-warningText bg-warningBg rounded px-2 py-1">
											⚠️ Acentos serão removidos automaticamente
										</div>
									)}
								</div>
							)}
						</div>

						<div>
							<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
								Descrição *
								<span className="text-xs text-grayText ml-2">
									(máx. 200 caracteres)
								</span>
							</label>
							<textarea
								value={selectedGroup ? (selectedGroup?.description || '') : (selectedMedia?.description || '')}
								onChange={(e) => {
									const value = e.target.value.slice(0, 200);
									if (selectedGroup) {
										setSelectedGroup(prev => ({ ...prev, description: value }));
									} else {
										setSelectedMedia(prev => ({ ...prev, description: value }));
									}
								}}
								maxLength={200}
								rows={3}
								className="w-full rounded-lg border border-emeraldBase/25 bg-grayContent/30 backdrop-blur-xl px-4 py-3 text-textPrimary focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText resize-none"
							/>
							{(selectedGroup?.description || selectedMedia?.description) && (
								<div className="mt-1 flex justify-between text-xs">
									<span className="text-grayText">
										Caracteres: {(selectedGroup?.description || selectedMedia?.description || '').length}/200
									</span>
									{(selectedGroup?.description || selectedMedia?.description || '').length > 180 && (
										<span className="text-warningText">
											Próximo do limite
										</span>
									)}
								</div>
							)}
						</div>

						{/* Seção de gerenciamento de arquivos para grupos */}
						{selectedGroup && (
							<div>
								<div className="flex items-center justify-between mb-2">
									<label className="block text-sm font-medium text-textOnDarkStrong">
										Arquivos do Grupo ({selectedGroup.mediaItems?.length || 0})
									</label>
									<button
										type="button"
										onClick={async () => {
											if (fileSelectionInProgress) {
												return;
											}

											setFileSelectionInProgress(true);
											try {
												const result = await ipcRenderer.invoke('select-ai-media-files-batch', {
													mediaTypes: ['image', 'video', 'audio', 'document']
												});
												
												if (result.success && !result.canceled && result.filePaths.length > 0) {
													for (const filePath of result.filePaths) {
														const filename = filePath.split('/').pop() || filePath.split('\\').pop();
														const extension = filename.toLowerCase().split('.').pop();
														let mediaType = 'document';
														if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
															mediaType = 'image';
														} else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
															mediaType = 'video';
														} else if (['mp3', 'ogg', 'opus', 'm4a', 'wav', 'aac', 'flac', 'wma'].includes(extension)) {
															mediaType = 'audio';
														}
														
														try {
															const addResult = await ipcRenderer.invoke('add-media-to-group', {
																instanceId,
																groupId: selectedGroup.groupId || selectedGroup.id,
																filePath,
																mediaType
															});
															
															if (addResult.success) {
																toast.success(`Arquivo ${filename} adicionado com sucesso!`);
															} else {
																toast.error(`Erro ao adicionar ${filename}: ${addResult.error}`);
															}
														} catch (error) {
															console.error(`Erro ao adicionar ${filename}:`, error);
															toast.error(`Erro ao adicionar ${filename}`);
														}
													}
													
													// Recarregar lista principal
													loadMediaList();
													
													// Recarregar dados atualizados do grupo no modal
													try {
														const groupResult = await ipcRenderer.invoke('list-ai-media', instanceId);
														if (groupResult.success) {
															const updatedGroup = groupResult.mediaGroups.find(g => 
																(g.groupId || g.id) === (selectedGroup.groupId || selectedGroup.id)
															);
															if (updatedGroup) {
																setSelectedGroup({ ...updatedGroup });
															}
														}
													} catch (error) {
														console.error('Erro ao atualizar dados do grupo:', error);
													}
												}
											} catch (error) {
												console.error('Erro ao selecionar arquivos:', error);
												toast.error('Erro ao selecionar arquivos');
											} finally {
												setFileSelectionInProgress(false);
											}
										}}
										className="flex items-center gap-1 px-2 py-1 bg-emeraldPrimary hover:bg-emeraldDeep text-textPrimary rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
										disabled={loading || fileSelectionInProgress}
									>
										<Plus className="h-3 w-3" />
										Adicionar
									</button>
								</div>
								
								{selectedGroup.mediaItems && selectedGroup.mediaItems.length > 0 ? (
									<div className="bg-grayContent/20 rounded-lg p-3 border border-emeraldBase/25 max-h-48 overflow-y-auto">
										<div className="space-y-2">
											{selectedGroup.mediaItems.map((media, index) => (
												<div key={media.id || index} className="flex items-center gap-3 p-2 bg-dashboardAccent/10 rounded-lg">
													<div className={`${getMediaTypeColor(media.mediaType || media.type)}`}>
														{getMediaIcon(media.mediaType || media.type)}
													</div>
													<div className="flex-1">
														<p className="text-sm font-medium text-textPrimary truncate">{media.filename || 'Arquivo não encontrado'}</p>
														<span className="text-xs text-emeraldLight capitalize">{media.mediaType || media.type}</span>
													</div>
													<button
														type="button"
														onClick={async () => {
															if (!confirm(`Tem certeza que deseja remover o arquivo "${media.filename}"?`)) {
																return;
															}
															
															try {
																const result = await ipcRenderer.invoke('remove-media-from-group', {
																	instanceId,
																	groupId: selectedGroup.groupId || selectedGroup.id,
																	mediaId: media.id
																});
																
																if (result.success) {
																	toast.success(`Arquivo ${media.filename} removido com sucesso!`);
																	
																	// Atualizar o selectedGroup removendo o item
																	setSelectedGroup(prev => ({
																		...prev,
																		mediaItems: prev.mediaItems.filter(item => item.id !== media.id)
																	}));
																	
																	// Recarregar lista principal
																	loadMediaList();
																	
																	// Se era o último arquivo, fechar o modal
																	if (selectedGroup.mediaItems.length <= 1) {
																		setShowEditModal(false);
																		setSelectedGroup(null);
																	}
																} else {
																	toast.error(result.error || 'Erro ao remover arquivo');
																}
															} catch (error) {
																console.error('Erro ao remover arquivo:', error);
																toast.error('Erro ao remover arquivo');
															}
														}}
														className="p-1 text-grayText hover:text-redBase transition-colors"
														title={`Remover ${media.filename}`}
														disabled={loading}
													>
														<Trash2 className="h-3 w-3" />
													</button>
												</div>
											))}
										</div>
									</div>
								) : (
									<div className="bg-grayContent/20 rounded-lg p-6 border border-emeraldBase/25 text-center">
										<Image className="h-8 w-8 mx-auto mb-2 text-grayText opacity-50" />
										<p className="text-sm text-grayText">Nenhum arquivo no grupo</p>
										<p className="text-xs text-grayText mt-1">Clique em "Adicionar" para incluir arquivos</p>
									</div>
								)}
							</div>
						)}
					</div>

					{/* Botões de ação - sempre visíveis na parte inferior */}
					<div className="flex gap-3 pt-4 border-t border-modalOverlayMedium mt-4 relative z-10 bg-modalBg/80 backdrop-blur-sm">
						<button
								type="button"
								onClick={() => {
									setShowEditModal(false);
									setSelectedMedia(null);
								}}
								className="flex-1 rounded-xl text-textPrimary px-4 py-3 font-medium transition-all duration-300 bg-gradient-to-r from-dangerFrom to-dangerTo hover:from-redMedium hover:to-redBase shadow-lg hover:shadow-dangerFrom/25 hover:scale-[1.02] cursor-pointer"
							>
								<span className="relative z-10">Cancelar</span>
							</button>
							<button
								type="button"
								onClick={handleUpdateMedia}
								className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emeraldPrimary/80 to-cyanPrimary/80 backdrop-blur-xl border border-emeraldLight/60 px-4 py-3 text-sm font-semibold text-textPrimary transition-all duration-300 hover:from-emeraldMedium/90 hover:to-cyanSecondary/90 hover:border-emeraldBase/70 hover:shadow-xl hover:shadow-emeraldBase/40 shadow-lg shadow-emeraldBase/25 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={loading}
							>
								{loading ? (
									<>
										<svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
										</svg>
										<span>Salvando...</span>
									</>
								) : (
									<>
										<svg className="h-4 w-4 drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
										</svg>
										<span>Salvar</span>
									</>
								)}
							</button>
						</div>
				</DialogContent>
			</Dialog>

			{/* Modal para visualizar mídia */}
			<Dialog open={showPreviewModal && selectedMedia} onOpenChange={(open) => {
				if (!open) {
					setShowPreviewModal(false);
					setSelectedMedia(null);
				}
			}}>
				<DialogContent
					className="max-w-[500px] bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-blackBase/50 relative overflow-hidden"
					style={{
						position: 'fixed',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						zIndex: 9999
					}}
				>
					{/* Efeitos de fundo glassmorphism */}
					<div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
					<div className="absolute top-16 left-24 w-32 h-32 bg-effectBlue rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>
					<div className="absolute bottom-16 right-16 w-36 h-36 bg-effectPurple rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>

					{/* Botão X customizado */}
					<button
						type="button"
						onClick={() => {
							setShowPreviewModal(false);
							setSelectedMedia(null);
						}}
						className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-textPrimary hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
					>
						<X className="w-4 h-4" />
					</button>

					<DialogHeader className="relative z-10">
						<DialogTitle className="flex items-center gap-4 text-textPrimary">
						<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-grayContent/80 via-statusInfo/30 to-purplePrimary/40 shadow-lg border border-modalOverlayMedium">
								<span className="text-xl drop-shadow-sm">👁️</span>
								<div className="absolute inset-0 bg-gradient-to-br from-statusInfo/10 to-purplePrimary/8 rounded-xl animate-pulse-subtle"></div>
							</div>
							<span className="text-xl font-semibold">Visualizar Mídia</span>
						</DialogTitle>
					</DialogHeader>

					<div className="flex flex-col gap-6 relative z-10">
						<div className="flex items-center gap-3">
							<div className={`${getMediaTypeColor(selectedMedia?.mediaType || selectedMedia?.type)}`}>
								{getMediaIcon(selectedMedia?.mediaType || selectedMedia?.type)}
							</div>
							<div>
								<h4 className="font-medium text-textPrimary">{selectedMedia?.title}</h4>
								<span className="text-sm text-textOnDarkMedium capitalize">{selectedMedia?.mediaType || selectedMedia?.type}</span>
							</div>
						</div>

						<div>
							<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
								Descrição
							</label>
							<div className="p-3 bg-grayContent/30 rounded-lg text-textOnDarkMedium text-sm">
								{selectedMedia?.description}
							</div>
						</div>

						<div>
							<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
								Arquivo
							</label>
							<div className="p-3 bg-grayContent/30 rounded-lg text-textOnDarkMedium text-sm font-mono break-all">
								{selectedMedia?.filename || 'N/A'}
							</div>
						</div>

						{selectedMedia?.createdAt && (
							<div>
								<label className="block text-sm font-medium text-textOnDarkStrong mb-2">
									Criado em
								</label>
								<div className="p-3 bg-grayContent/30 rounded-lg text-textOnDarkMedium text-sm">
									{new Date(selectedMedia.createdAt).toLocaleString('pt-BR')}
								</div>
							</div>
						)}
					</div>
				</DialogContent>
			</Dialog>


		</>
	);
};

export default AIMediaSection;
